import { Injectable } from '@angular/core';
import { Observable, of, forkJoin, map, Subject } from 'rxjs';
import { AnalyticsApiService, BackendService, Dimension, DimensionProperty, ObjectBaseService, PublicLinkService, Query, QueryFilter, QueryResult, RequestOptionsArgs, Schema, WidgetQueueProxyService, DateRange, DateRangeValue, DatesService, BenchmarkService } from '@hub-components';
import { DataSourceResult } from '../../interfaces/data-source-result.interface';
import { WidgetConfig } from '../../interfaces/widget-config.interface';
import { Config, DataSourceConfig } from '../../models/config.model';
import { MapStateDrilldown } from '../../models/map-state-drilldown.model';
import { Snapshot } from '../../models/snapshot.model';
import { DEFAULT_DATE_INTERVAL, MAP_TYPES } from '../../snapshot-constants';
import { Params } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class SnapshotService extends ObjectBaseService {
  private reloadQueries$ = new Subject<void>();

  constructor(
    private analyticsApiService: AnalyticsApiService,
    private widgetQueueProxyService: WidgetQueueProxyService,
    private backendService: BackendService,
    private publicLinkService: PublicLinkService,
    private datesService: DatesService,
    private benchmarkService: BenchmarkService,
  ) {
    super();
  }

  public getDateRangeValue(
    queryParams: Params,
    isPublicLink: boolean,
  ): DateRangeValue {
    if (isPublicLink) {
      if (queryParams.dateRange) {
        try {
          const parsed = JSON.parse(atob(queryParams.dateRange)) as DateRangeValue;

          if (parsed.type === 'fixed') {
            return {
              ...parsed,
              startDate: new Date(parsed.startDate),
              endDate: new Date(parsed.endDate),
            };
          }

          return parsed;
        } catch {
          return DEFAULT_DATE_INTERVAL;
        }
      } else if (queryParams.interval) {
        return this.getDateRangeValueFromInterval(queryParams.interval);
      } else {
        return DEFAULT_DATE_INTERVAL;
      }
    }

    return DEFAULT_DATE_INTERVAL;
  }

  public getFlightDateRange(flightDatesParam: string): DateRange | null {
    if (!flightDatesParam || flightDatesParam === 'none') {
      return null;
    }

    const dateRange = this.datesService.intervalToFixedDateRange(flightDatesParam);

    if (!dateRange.startDate || !dateRange.endDate) {
      return null;
    }

    return {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };
  }

  public getFlightDatesPreview(flightDateRange: DateRange): string {
    return this.datesService.intervalDateRange({
      type: 'fixed',
      startDate: flightDateRange.startDate,
      endDate: flightDateRange.endDate,
    });
  }

  public calculateComparisonInterval(
    dates: DateRangeValue,
    comparisonPeriod: string,
  ): string | null {
    if (!comparisonPeriod || comparisonPeriod === 'none') {
      return null;
    }

    const comparisonDates = this.benchmarkService.calculateBenchmark(dates, comparisonPeriod);
    return comparisonDates
      ? this.datesService.fixedQueryInterval(this.datesService.parseValue(comparisonDates as DateRangeValue))
      : null;
  }

  public createSnapshot(itemId: string, itemName: string, type: string, { overviewNote, breakdownNote }: { overviewNote?: string, breakdownNote?: string }, mapStateDrilldowns?: MapStateDrilldown[]): Observable<Snapshot> {
    const snapshot = new Snapshot({
      item_id: itemId,
      item_name: itemName,
      type,
      overview_note: overviewNote,
      breakdown_note: breakdownNote,
      map_state_drilldowns: mapStateDrilldowns?.map(drilldown => drilldown.asJson()),
    });

    return this.create(snapshot);
  }

  public saveMapDrilldownState(snapshot: Snapshot | null, itemId: string, itemName: string, itemDimension: string, widgetId: number, stateName: string): Observable<Snapshot> {
    if (snapshot) {
      snapshot.mapStateDrilldowns ??= [];
      const existingDrilldown = snapshot.mapStateDrilldowns.find(drilldown => drilldown.widgetId === widgetId);
      if (existingDrilldown) {
        existingDrilldown.state = stateName;
      } else {
        snapshot.mapStateDrilldowns.push(new MapStateDrilldown({ widget_id: widgetId, state: stateName }));
      }
      return this.update(snapshot);
    } else {
      const mapStateDrilldowns = [new MapStateDrilldown({ widget_id: widgetId, state: stateName })];
      return this.createSnapshot(itemId, itemName, itemDimension, {}, mapStateDrilldowns);
    }
  }

  public update(snapshot: Snapshot): Observable<Snapshot> {
    return this.backendService.update(`snapshots/${snapshot.id}`, snapshot.asJson())
      .pipe(
        map(res => new Snapshot(res)),
      );
  }

  public load(itemId: string, type: string): Observable<Snapshot> {
    return this.backendService.load(`snapshots/${itemId}`, { params: { type }, skipLoader: true, throwErrors: true })
      .pipe(
        map(res => res ? new Snapshot(res) : null),
      );
  }

  public loadPublic(uuid: string, password?: string): Observable<Snapshot> {
    return this.backendService.create(`s/${uuid}/show`, { password }, { throwErrors: true }).pipe(
      map(res => new Snapshot(res)),
    );
  }

  public checkIfSharedWithPublicLink(publicUuid: string): Observable<boolean> {
    return this.publicLinkService.exists(publicUuid).pipe(
      map(res => res['uuid'] === publicUuid),
    );
  }

  public saveCardVisibility(datasource_id: number, hidden: boolean, itemId: string): Observable<void> {
    return this.backendService.update(`card_visibilities/${datasource_id}`, { hidden, item_id: itemId });
  }

  public generateQuery(dataSource: string, dimensions: string[], dimensionProperties: {}, metrics: string[], filters: { dimension: string, value: string }[], rollup: string, schema: Schema, interval: string, spline?: string[], auth?: string, limit?: number): Query {
    const order = [];
    if (limit > 0) {
      order.push(`-${metrics[0]}`);
    }
    const query = new Query({
      dataset: dataSource,
      dimensions: dimensions,
      dimensionProperties: dimensionProperties,
      metrics: [...metrics, ...(spline ?? [])],
      interval: interval,
      rollup: rollup,
      limit: limit,
      order: order,
    }, schema);

    query.filters = filters.map(filter => ({
      expressions: [
        new QueryFilter({
          dimension: new Dimension(filter.dimension || 'advertiser'),
          dimension_property: new DimensionProperty('id', null, true, 'id'),
          values: filter.value,
          type: 'any',
        }),
      ],
    }));

    query.auth = auth;

    return query;
  }

  public runQuery(query: Query): Observable<QueryResult> {
    return this.widgetQueueProxyService.analiticsQueueProxy(query, { cache: true, skipLoader: true, throwErrors: true });
  }

  public cancelQueries() {
    this.widgetQueueProxyService.cancelQueryQueue();
  }

  public triggerQueryReload() {
    this.reloadQueries$.next();
  }

  public onQueryReloadTrigger(): Observable<void> {
    return this.reloadQueries$.asObservable();
  }

  public loadSchemas(datasets: string[], options: RequestOptionsArgs = {}): Observable<Record<string, Schema>> {
    if (!datasets.length) {
      return of({});
    }

    return forkJoin(
      datasets.reduce((acc, dataset) => {
        acc[dataset] = this.analyticsApiService.loadSchema(dataset, options);
        return acc;
      }, {} as Record<string, Observable<Schema>>),
    );
  }

  public createQueryObservables(dataSources: DataSourceConfig[], queryResults: DataSourceResult[], schemas: Record<string, Schema>, itemId: string, interval: string, snapshot?: Snapshot): Observable<QueryResult>[] {
    const queryObservables: Observable<QueryResult>[] = [];
    dataSources.forEach(dataSourceConfig => {
      dataSourceConfig.overview?.pages?.forEach((page, pageIndex) => {
        page.widgets.forEach((widgetConfig: WidgetConfig) => {
          const queryObservable = this.runWidgetQuery(queryResults, schemas, itemId, interval, dataSourceConfig, widgetConfig, pageIndex, snapshot);
          queryObservables.push(queryObservable);
        });
      });
    });
    return queryObservables;
  }

  public runWidgetQuery(queryResults: DataSourceResult[], schemas: Record<string, Schema>, itemId: string, interval: string, dataSourceConfig: DataSourceConfig, widgetConfig: WidgetConfig, pageIndex: number, snapshot?: Snapshot): Observable<QueryResult> {
    let dataSourceEntry = queryResults.find(entry => entry.index === dataSourceConfig.index);

    if (!dataSourceEntry.pages[pageIndex]) {
      dataSourceEntry.pages[pageIndex] = [];
    }

    dataSourceEntry.pages[pageIndex][widgetConfig.index] = null;

    const queryFilters = [{ dimension: dataSourceConfig.dimension, value: itemId }];

    if (widgetConfig.inputs.filter) {
      queryFilters.push(widgetConfig.inputs.filter);
    }

    let dimensions = [widgetConfig.inputs.dimension];

    if (widgetConfig.options?.map?.mapType === MAP_TYPES.US_ZIP_CODES_BY_STATE && widgetConfig.options?.map?.stateDimension) {
      const hasDrilldown = snapshot?.mapStateDrilldowns?.find(drilldown => drilldown.widgetId === widgetConfig.id);
      if (hasDrilldown) {
        queryFilters.push({ dimension: widgetConfig.options.map.stateDimension, value: hasDrilldown.state });
      }
    }

    const query = this.generateQuery(
      dataSourceConfig.name,
      dimensions,
      {},
      widgetConfig.inputs.metrics,
      queryFilters,
      widgetConfig.rollup,
      schemas[dataSourceConfig.name],
      interval,
      widgetConfig.inputs.spline,
      widgetConfig.auth,
      widgetConfig.inputs.limit,
    );

    if (dataSourceConfig.filter?.length) {
      query.filters = [...query.filters, ...dataSourceConfig.filter];
    }

    return this.runQuery(query)
      .pipe(
        map((result: QueryResult) => {
          dataSourceEntry.pages[pageIndex][widgetConfig.index] = {
            data: result.rows?.length ? result : null,
            config: widgetConfig,
          };
          return result;
        }),
      );
  }

  public exportOverviewScreenshot(itemId: string, itemName: string, itemDimension: string, interval: string, datasourceId: number, pageIndex: number, kpiSelections?: string): Observable<any> {
    return this.backendService.create(`${itemId}/export_screenshot`, {
      item_name: itemName,
      datasource_id: datasourceId,
      page_index: pageIndex,
      interval: interval,
      item_dimension: itemDimension,
      view: 'overview',
      kpi_selections: kpiSelections,
    });
  }

  public exportBreakdownScreenshot(itemId: string, itemName: string, itemDimension: string, interval: string, datasourceId: number): Observable<any> {
    return this.backendService.create(`${itemId}/export_screenshot`, { item_name: itemName, interval: interval, datasource_id: datasourceId, item_dimension: itemDimension, view: 'breakdown' });
  }

  public accountUsers() {
    return of([]);
  }

  public appUsers() {
    return of([]);
  }

  public appSuites() {
    return of([]);
  }

  private getDateRangeValueFromInterval(intervalParam: string | undefined): DateRangeValue {
    if (!intervalParam) {
      return DEFAULT_DATE_INTERVAL;
    }

    const fixedDateRange = this.datesService.intervalToFixedDateRange(intervalParam);

    if (Number.isNaN(fixedDateRange.startDate?.getTime()) || Number.isNaN(fixedDateRange.endDate?.getTime())) {
      return DEFAULT_DATE_INTERVAL;
    }

    return fixedDateRange;
  }

  private create(snapshot: Snapshot): Observable<Snapshot> {
    return this.backendService.create('/snapshots', snapshot.asJson())
      .pipe(
        map(res => new Snapshot(res)),
      );
  }
}
